// src/pages/DashboardPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DashboardFilters from "../components/dashboard/DashboardFilters";
import DashboardOverviewSection from "../components/dashboard/DashboardOverviewSection";
import DashboardBlockSection from "../components/dashboard/DashboardBlockSection";
import { dashboardBlocksConfig } from "../components/dashboard/dashboardBlocksConfig";
import useDashboardOverviewData from "../hooks/dashboard/useDashboardOverviewData";
import useDashboardBlocksData from "../hooks/dashboard/useDashboardBlocksData";
import {
  DEFAULT_DASHBOARD_FILTERS,
  getDashboardSearchParamPatch,
  normalizeCenterCode,
} from "../utils/dashboard/queryParams";

import {
  fetchCenters,
  fetchAllCenters,
} from "../services/dashboardService";

export default function DashboardPage() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();

  const [centers, setCenters] = useState([]); // dashboard centers (selector)
  const [centersCatalog, setCentersCatalog] = useState([]); // DB centers (name/code)

  const [filters, setFilters] = useState(() => ({ ...DEFAULT_DASHBOARD_FILTERS }));
  const [err, setErr] = useState(null);

  // ✅ Soporte de entrada desde minimapa: /dashboard?center=...&from=...&to=...
  useEffect(() => {
    const patch = getDashboardSearchParamPatch(sp);
    if (!patch) return;

    setFilters((prev) => ({
      ...prev,
      ...patch,
    }));
    // solo en mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MAPA code(normalizado) -> name
  const centerNameByCode = useMemo(() => {
    const m = {};
    for (const c of centersCatalog || []) {
      const code = normalizeCenterCode(c.code);
      if (code) m[code] = c.name;
    }
    return m;
  }, [centersCatalog]);

  // 1) cargar listas (dashboard centers + catálogo)
  useEffect(() => {
    (async () => {
      try {
        setErr(null);
        const [dashCenters, catalog] = await Promise.all([
          fetchCenters(),
          fetchAllCenters(),
        ]);

        const dashItems = dashCenters?.items || [];
        const catalogItems = Array.isArray(catalog)
          ? catalog
          : catalog?.items || [];

        setCenters(dashItems);
        setCentersCatalog(catalogItems);
      } catch (e) {
        setErr(e?.message || "Error carregant centres");
      }
    })();
  }, []);
  const {
    loading,
    kpis,
    tsSurveys,
    tsTemp,
    yesno,
    yesnoByKey,
    topSoil,
    topAnimals,
    topWaste,
  } = useDashboardOverviewData(filters, setErr);

  const {
    bloc0Stats,
    bloc0Loading,
    bloc0Err,
    bloc0SoilSeries,
    bloc1Stats,
    bloc1Loading,
    bloc1Err,
    bloc1PhSeries,
    bloc2Stats,
    bloc2Loading,
    bloc2Err,
    bloc3Stats,
    bloc3Loading,
    bloc3Err,
    bloc4Stats,
    bloc4Loading,
    bloc4Err,
    bloc5Stats,
    bloc5Loading,
    bloc5Err,
    bloc6Stats,
    bloc6Loading,
    bloc6Err,
    bloc7Stats,
    bloc7Loading,
    bloc7Err,
  } = useDashboardBlocksData(filters);

  return (
    <div className="min-h-screen bg-[#E8E5D3]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold">
            {t("dashboard.page.title", "LivingSoils · Tauler")}
          </h1>
          <p className="text-sm text-gray-600">
            {t(
              "dashboard.page.subtitle",
              "Agregacions per centre i rang de dates (sense descarregar enquestes crues)."
            )}
          </p>
        </div>

        <DashboardFilters
          centers={centers}
          value={filters}
          onChange={setFilters}
          centerNameByCode={centerNameByCode}
          normalizeCenterCode={normalizeCenterCode}
        />

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-4">
            {err}
          </div>
        )}

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            {t("dashboard.page.loading", "Carregant…")}
          </div>
        ) : (
          <>
            <DashboardOverviewSection
              kpis={kpis}
              yesnoByKey={yesnoByKey}
              yesnoItems={yesno?.items || []}
              tsSurveys={tsSurveys}
              tsTemp={tsTemp}
              topSoil={topSoil}
              topAnimals={topAnimals}
              topWaste={topWaste}
            />

            <section className="pb-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-900">
                  {t("dashboard.page.blocksTitle", "Seccions per bloc")}
                </h2>
                <p className="text-sm text-slate-600">
                  {t(
                    "dashboard.page.blocksSubtitle",
                    "Estructura preparada per incorporar estadístiques específiques de cada bloc sense alterar la visió general actual."
                  )}
                </p>
              </div>

              <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-4">
                {dashboardBlocksConfig.map((block) => (
                  <DashboardBlockSection
                    key={block.id}
                    {...block}
                    mode={
                      block.id === "bloc0" ||
                      block.id === "bloc1" ||
                      block.id === "bloc2" ||
                      block.id === "bloc3" ||
                      block.id === "bloc4" ||
                      block.id === "bloc5" ||
                      block.id === "bloc6" ||
                      block.id === "bloc7"
                        ? "live"
                        : "placeholder"
                    }
                    data={
                      block.id === "bloc0"
                        ? bloc0Stats
                        : block.id === "bloc1"
                        ? bloc1Stats
                        : block.id === "bloc2"
                        ? bloc2Stats
                        : block.id === "bloc3"
                        ? bloc3Stats
                        : block.id === "bloc4"
                        ? bloc4Stats
                        : block.id === "bloc5"
                        ? bloc5Stats
                        : block.id === "bloc6"
                        ? bloc6Stats
                        : block.id === "bloc7"
                        ? bloc7Stats
                        : null
                    }
                    charts={
                      block.id === "bloc0"
                        ? { soilSeries: bloc0SoilSeries }
                        : block.id === "bloc1"
                        ? { phSeries: bloc1PhSeries }
                        : null
                    }
                    loading={
                      block.id === "bloc0"
                        ? bloc0Loading
                        : block.id === "bloc1"
                        ? bloc1Loading
                        : block.id === "bloc2"
                        ? bloc2Loading
                        : block.id === "bloc3"
                        ? bloc3Loading
                        : block.id === "bloc4"
                        ? bloc4Loading
                        : block.id === "bloc5"
                        ? bloc5Loading
                        : block.id === "bloc6"
                        ? bloc6Loading
                        : block.id === "bloc7"
                        ? bloc7Loading
                        : false
                    }
                    error={
                      block.id === "bloc0"
                        ? bloc0Err
                        : block.id === "bloc1"
                        ? bloc1Err
                        : block.id === "bloc2"
                        ? bloc2Err
                        : block.id === "bloc3"
                        ? bloc3Err
                        : block.id === "bloc4"
                        ? bloc4Err
                        : block.id === "bloc5"
                        ? bloc5Err
                        : block.id === "bloc6"
                        ? bloc6Err
                        : block.id === "bloc7"
                        ? bloc7Err
                        : null
                    }
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
